/**
 * Response shapes for Labeeb's admin API — all 16 endpoints.
 *
 * From docs/products/labeeb-api-reference.md. Every field is optional or
 * nullable on purpose: client.ts does a blind `(await resp.json()) as T`, so
 * nothing here is guaranteed at runtime. A truncated or renamed response must
 * degrade through a helper's floor rather than crash a `.map` call site — the
 * failure mode that has bitten this console four times against MCP.
 *
 * Mirrors api/mcp.ts. Helpers are prefixed `labeeb…` where mcp.ts already owns
 * the plain name, so a page can import from both without aliasing.
 */

// ---------------------------------------------------------------- catalog --

/**
 * Every valid value a write on this API accepts (`CatalogOut`). Fetch once and
 * use it to populate pickers rather than hard-coding lists.
 *
 * NO `agents`, NO `flags`, NO `audit_domains` any more — the first two have no
 * concept left behind them and the third is fixed by the route's own enum.
 * What it carries instead is the domain and plugin vocabulary, the rule
 * targets, and `editable`, which names every writable resource with its route
 * and fields.
 *
 * `tiers` IS AN OBJECT, NOT AN ARRAY. It used to be a list of
 * `{tier_id, display_name}` pairs; it is now a page summary
 * (`{default_tier, first_page, more}`) carrying bare tier ids. Anything that
 * called `.find` on it crashed — which is exactly the shape drift CLAUDE.md
 * warns about, and it did crash this console once before this type was fixed.
 *
 * Everything optional per the usual `client.ts` cast note: a truncated
 * catalog should degrade every picker to empty, not crash one.
 */
export interface LabeebCatalog {
  admin_api_version?: string;
  model_version?: string;
  settings_schema_version?: string;
  domains?: string[];
  plugins?: string[];
  rule_targets?: string[];
  tiers?: { default_tier?: string; first_page?: string[]; more?: boolean; listed_at?: string };
  editable?: { resource?: string; route?: string; fields?: string[] }[];
  /** The enums a write may name. Populate pickers from these, never hardcode. */
  values?: {
    user_statuses?: string[];
    business_statuses?: string[];
    rule_effects?: string[];
  };
}

/** One selectable tier. There is no display name on this API; the id is the label. */
export interface LabeebTierRef {
  tier_id: string;
  display_name?: string;
}

/**
 * The tiers a picker may offer, as `{tier_id}` rows.
 *
 * Reads `tiers.first_page`, which is a list of IDS. It is a FIRST PAGE: when
 * `more` is true the catalog has not told us about every tier, so a picker
 * built from this is incomplete rather than wrong — `labeebMoreTiers` says
 * which case applies instead of leaving a caller to assume.
 */
export function tierRefsOf(c: LabeebCatalog | undefined | null): LabeebTierRef[] {
  return (c?.tiers?.first_page ?? []).map((tier_id) => ({ tier_id }));
}

/** True when the catalog's tier list is known to be partial. */
export function labeebMoreTiers(c: LabeebCatalog | undefined | null): boolean {
  return c?.tiers?.more === true;
}

/** The tier a business lands on with none of its own, or null. */
export function labeebDefaultTier(c: LabeebCatalog | undefined | null): string | null {
  return c?.tiers?.default_tier ?? null;
}

/** The domains this API will accept, `[]` when unreadable. */
export function labeebDomainsOf(c: LabeebCatalog | undefined | null): string[] {
  return c?.domains ?? [];
}

/**
 * A tier's label.
 *
 * THERE IS NO DISPLAY NAME on this API — `TierOut` and the catalog both carry
 * the id alone. This kept its name and signature so ~8 call sites did not
 * have to change, and it now returns the id, which is also what a write
 * submits. Previously it searched an array of `{tier_id, display_name}` pairs;
 * `tiers` became an object and `.find` on it threw.
 */
export function tierDisplayName(c: LabeebCatalog | undefined | null, tierId: string): string {
  return tierRefsOf(c).find((t) => t.tier_id === tierId)?.display_name ?? tierId;
}

// ---------------------------------------------------------------- tenants --

/**
 * The merchant's identity, as Labeeb records it.
 *
 * NULLABLE on `TenantOut`: a business row can exist before its profile is
 * resolved, so a name is not guaranteed even for a tenant Labeeb knows.
 */
export interface LabeebProfile {
  business_name?: string;
  /** The Foodics business reference under its BI name. */
  bi_business_id?: string | null;
  currency?: string;
  timezone?: string;
  owner_locale?: string | null;
  updated_at?: string;
}

/**
 * One business, as the list route returns it (`TenantOut`).
 *
 * NO TIER AND NO AGENTS. The old shape carried `tier_id`, `allowed_agents` and
 * `beta_agents` on every list row; the new list carries identity and standing
 * only. A tier now comes from `GET /admin/tenant/{id}/quota`, and agents are
 * gone from the product entirely — `enabled_domains` on
 * `GET /admin/tenant/{id}/settings` replaces them.
 *
 * `standing_version` is here because it is what a suspend/resume write must
 * send as `expected_version`: the list is enough to act from, without a
 * second read.
 */
export interface LabeebTenantSummary {
  tenant_id: string;
  /** `active` | `suspended`. A string, not a union — the server owns the set. */
  status?: string;
  standing_version?: number;
  profile?: LabeebProfile | null;
}

/**
 * One page of businesses (`TenantPageOut`).
 *
 * CURSOR-PAGINATED. `next_cursor` is non-null when more pages exist; the
 * console currently reads the first page only, which is why `next_cursor` is
 * surfaced rather than ignored — a caller that drops it silently shows a
 * truncated fleet as if it were the whole one.
 */
export interface LabeebTenantList {
  /**
   * Optional for the usual `client.ts` reason — nothing here is actually
   * guaranteed at runtime. Read through `labeebTenantsOf`, never this field
   * directly.
   */
  items?: LabeebTenantSummary[];
  next_cursor?: string | null;
}

/**
 * `data.items`, defaulting to `[]` rather than throwing on `.filter`/`.map`.
 *
 * Named `labeebTenantsOf`, not `tenantsOf` — mcp.ts already owns that name,
 * and a page importing both products' tenant helpers must never need to alias.
 *
 * The field it reads changed from `tenants` to `items` when Labeeb's admin API
 * was replaced. Keeping the helper's NAME and SIGNATURE is what let that
 * change stay inside this module.
 */
export function labeebTenantsOf(l: LabeebTenantList | undefined | null): LabeebTenantSummary[] {
  return l?.items ?? [];
}

/** The cursor for the next page, or null when this is the last one. */
export function labeebNextCursor(l: { next_cursor?: string | null } | undefined | null): string | null {
  return l?.next_cursor ?? null;
}

/**
 * One business's usage counters (`UsageOut`), read-only.
 *
 * Flat integers now, not the old nested `{daily: {messages, limit}}`. The
 * window boundaries are served too (`day_resets_at`, `month_resets_at`), so a
 * panel can say when a merchant's allowance returns instead of implying the
 * limit is permanent.
 */
export interface LabeebUsage {
  tenant_id?: string;
  tier_id?: string;
  day?: string;
  messages_today?: number;
  daily_limit?: number;
  day_resets_at?: string;
  month?: string;
  messages_this_month?: number;
  monthly_limit?: number;
  month_resets_at?: string;
  timezone?: string;
  /** Per-domain turn counts, or null when not recorded. */
  /**
   * Per-domain turn counts (`DomainTurnsOut`), or null.
   *
   * NULL UNLESS `?date=YYYY-MM-DD` IS PASSED — this console does not pass it,
   * so today it is always null and the panel renders nothing. Typed properly
   * anyway: the shape was guessed as `{items}` when it is actually two
   * separate windows, and a `.map` over the wrong key is the crash this file
   * exists to prevent.
   */
  domain_turns?: LabeebDomainTurns | null;
}

/** One window's per-domain counts. */
export interface LabeebDomainTurns {
  day?: string;
  month?: string;
  daily?: { domain: string; turns: number }[];
  monthly?: { domain: string; turns: number }[];
}

/** Today's per-domain turns, `[]` when absent — which is the normal case. */
export function domainTurnsTodayOf(u: LabeebUsage | undefined | null): {
  domain: string;
  turns: number;
}[] {
  return u?.domain_turns?.daily ?? [];
}

/**
 * One business's standing (`BusinessStandingOut`).
 *
 * `version` is what a write sends back as `expected_version`. `by`/`at`/
 * `reason` are the last change's attribution — null when the business has
 * never been suspended.
 */
export interface LabeebStanding {
  tenant_id?: string;
  /** `active` | `suspended`. */
  status?: string;
  version?: number;
  by?: string | null;
  at?: string | null;
  reason?: string | null;
}

/** Is this business suspended? Unknown standing is NOT treated as suspended. */
export function isSuspended(s: LabeebStanding | undefined | null): boolean {
  return s?.status === "suspended";
}

/** One domain, and whether anything above the tenant is holding it off. */
export interface LabeebEffectiveDomain {
  domain: string;
  enabled: boolean;
  /**
   * What is overriding the tenant's own choice — the environment, typically.
   * Null when the tenant's setting is what applies.
   *
   * This is the field the agent model could never express: it says WHY a
   * capability is off, not merely that it is.
   */
  restricted_by: string | null;
}

/** One plugin, same shape and same `restricted_by` meaning. */
export interface LabeebEffectivePlugin {
  plugin: string;
  enabled: boolean;
  restricted_by: string | null;
}

/** What actually applies once the environment's gates are folded in. */
export interface LabeebEffectiveSettings {
  domains?: LabeebEffectiveDomain[];
  plugins?: LabeebEffectivePlugin[];
  tenant_version?: number;
  environment_version?: number;
}

/**
 * One business's settings (`TenantSettingsView`).
 *
 * `settings.enabled_domains` is what the tenant ASKED for — `"all"` or a
 * list. `effective` is what RUNS. Render `effective`; a page that shows the
 * stored value alone tells an operator a domain is on when the environment
 * has it off.
 */
export interface LabeebTenantSettings {
  tenant_id?: string;
  /**
   * What the business ASKED for: the literal `"all"`, or a list of names from
   * `catalog.domains`.
   *
   * `[]` IS VALID AND MEANS NO DOMAIN ENABLED — not "unset". Collapsing the
   * two would tell an operator a business is on defaults when it has been
   * deliberately narrowed to nothing. Omitted/null on a PATCH means unchanged.
   */
  settings?: { enabled_domains?: string[] | string };
  effective?: LabeebEffectiveSettings;
  version?: number;
  by?: string | null;
  at?: string | null;
  reason?: string | null;
  schema_version?: string;
  model_version?: string | null;
}

/** The domains in force, `[]` when unreadable. */
export function effectiveDomainsOf(
  s: LabeebTenantSettings | undefined | null,
): LabeebEffectiveDomain[] {
  return s?.effective?.domains ?? [];
}

/** The plugins in force, `[]` when unreadable. */
export function effectivePluginsOf(
  s: LabeebTenantSettings | undefined | null,
): LabeebEffectivePlugin[] {
  return s?.effective?.plugins ?? [];
}

/**
 * One business's quota assignment (`QuotaOut`).
 *
 * `source` says whether the numbers are the tenant's own or inherited from
 * the tier — the distinction an operator needs before changing one, since
 * editing an inherited value creates an override where none existed.
 */
export interface LabeebTenantQuota {
  tenant_id?: string;
  tier_id?: string;
  daily?: number;
  monthly?: number;
  /** e.g. `tier` or `tenant`. */
  source?: string;
  default_tier?: string;
  version?: number;
}

/**
 * One business, as the detail route returns it (`TenantOut`) — the same shape
 * as a list row. The aggregate that bundles profile, quota, settings,
 * standing and usage together is `TenantSummaryOut`, served elsewhere.
 */
export interface LabeebTenantDetail {
  tenant_id?: string;
  status?: string;
  standing_version?: number;
  profile?: LabeebProfile | null;
}

/** The merchant's name from whichever shape carries a profile, or null. */
export function labeebBusinessName(
  t: { profile?: LabeebProfile | null } | undefined | null,
): string | null {
  return t?.profile?.business_name || null;
}

// ------------------------------------------------------------------ tiers --

/**
 * One tier's limits row.
 *
 * `source: "code_default"` means editing this tier CREATES a row rather than
 * changing one — there is no seeded row for it yet, only the code's fallback.
 * `limits: null` accompanies that case in the doc's own example: a
 * `code_default` tier has no stored limits to show.
 */
/**
 * One tier envelope (`TierOut`).
 *
 * FLAT NOW. The old shape nested every limit under `limits: {}` with a
 * `display_name` and a `source` saying whether a stored row existed. The new
 * API answers with two integers and a version — no nesting, and no
 * `code_default` distinction, because a tier either exists or the route 404s.
 *
 * `-1` means unlimited; a limit is never absent.
 */
export interface LabeebTierRow {
  tier_id: string;
  daily?: number;
  monthly?: number;
  is_default?: boolean;
  /** What a `PATCH` must send as `expected_version`. */
  version?: number;
}

/** One page of tier envelopes (`TierPageOut`). */
export interface LabeebTiers {
  /** Optional per the usual cast note. Read through `tierRowsOf`, never directly. */
  items?: LabeebTierRow[];
  next_cursor?: string | null;
}

/** `data.tiers`, defaulting to `[]`. */
export function tierRowsOf(t: LabeebTiers | undefined | null): LabeebTierRow[] {
  return t?.items ?? [];
}

/**
 * `PATCH /admin/tiers/{tier_id}` response.
 *
 * `warning` is present when a seed script in the product repo will REVERT
 * this change on its next run — always show it, it is the single most
 * important thing about this response. The shape beyond `tier_id`/`warning`
 * is otherwise unspecified by the doc, so extra keys pass through as
 * `unknown` rather than being dropped or asserted away.
 */
export interface LabeebPatchTierResponse {
  tier_id?: string;
  warning?: string;
  [key: string]: unknown;
}

// ------------------------------------------------------------------ flags --

/**
 * One feature flag's resolved state.
 *
 * `effective` is the field to RENDER, not `stored_value` — a flag with no
 * stored parameter still has a real behaviour, and it differs per flag
 * (`narration_enabled` defaults ON while unset, `narration_haiku_enabled`
 * defaults OFF). `stored_value` is `null` exactly when `exists` is false.
 */
export interface LabeebFlag {
  flag: string;
  exists: boolean;
  stored_value: boolean | null;
  effective: boolean;
  code_default: boolean;
}

/**
 * `PATCH /admin/flags/{flag}` — the response.
 *
 * `after` is the RAW STORED STRING, never a boolean: the runtime parses the
 * parameter as `== "true"`, so the API stores `"true"`/`"false"` and hands
 * that back. Rendering it as a boolean would be a second, lying source of
 * truth next to the next `GET /admin/flags`.
 */
export interface LabeebPatchFlagResponse {
  flag: string;
  before: string | null;
  after: string | null;
  created?: boolean;
  takes_effect?: string;
}

export interface LabeebFlags {
  flags?: LabeebFlag[];
  /** e.g. "Takes effect within ~60s…" — surface it, nothing here is instant. */
  takes_effect?: string;
}

/** `data.flags`, defaulting to `[]`. */
export function flagsOf(f: LabeebFlags | undefined | null): LabeebFlag[] {
  return f?.flags ?? [];
}

// ----------------------------------------------------------------- config --

/**
 * One live-config key: an app-level value the runtime re-reads without a
 * deploy. `value` is `unknown` because it varies by key (a string, a list, a
 * number) and a truncated/renamed response must not assert a shape that
 * isn't there. `source` describes where the CURRENT value comes from, e.g.
 * "yaml (deployed)" when `overridden` is false.
 */
export interface LabeebConfigKey {
  key: string;
  writable: boolean;
  overridden: boolean;
  value: unknown;
  source?: string;
  description?: string;
  takes_effect?: string;
  example?: unknown;
}

/**
 * A config key the console cannot make live, with the reason why — e.g.
 * "services_registry.__init__ copies it to self.quota_enabled…". Render
 * these VISIBLE but disabled, with the reason: hiding them loses information
 * an operator needs.
 */
export interface LabeebReadOnlyConfigKey {
  key: string;
  reason: string;
}

export interface LabeebConfig {
  keys?: LabeebConfigKey[];
  read_only?: LabeebReadOnlyConfigKey[];
  /** e.g. "A key with overridden=false is using the value deployed in the yaml…" */
  note?: string;
}

/** `data.keys`, defaulting to `[]`. */
export function configKeysOf(c: LabeebConfig | undefined | null): LabeebConfigKey[] {
  return c?.keys ?? [];
}

/**
 * `data.read_only`, defaulting to `[]`. Independent of `configKeysOf` — a
 * response carrying one list but not the other must not make either throw.
 */
export function readOnlyConfigKeysOf(
  c: LabeebConfig | undefined | null,
): LabeebReadOnlyConfigKey[] {
  return c?.read_only ?? [];
}

/**
 * `PATCH /admin/config/{key}` — the response.
 *
 * `after` is the STORED STRING, so a list comes back JSON-encoded
 * (`'["menu", "reporting"]'`) rather than as an array. Render it as the
 * server's own record of what it wrote, not as a parsed value — parsing it
 * back into a list would invent a shape the next GET may disagree with.
 */
export interface LabeebPatchConfigResponse {
  key: string;
  before: unknown;
  after: unknown;
  created?: boolean;
  takes_effect?: string;
}

/**
 * `DELETE /admin/config/{key}` — removes an override.
 *
 * This RESTORES behaviour rather than destroying it, which is why the console
 * labels it "reset to deployed value". `reverted_to` names what the key now
 * resolves to, and is the whole point of showing the result at all.
 */
export interface LabeebDeleteConfigResponse {
  key: string;
  before: unknown;
  after: unknown;
  reverted_to?: string;
}

// ------------------------------------------------------------------ audit --

/**
 * One admin action against Labeeb's config, flags, quota or tenant knobs.
 *
 * `actor` is nullable for the same reason `mcp.ts`'s `AuditEvent.actor` is:
 * an audit log is the last surface that should fall over, so a bare
 * `.replace`/`.split` on a row missing its actor must be a compile error
 * rather than a white screen. `before`/`after` are `unknown`, not a fixed
 * type, because the value shape is action-shaped — a string for
 * `tenant.set_tier`, a list for a `stable_agents` config write, a boolean for
 * a flag toggle — exactly like MCP's audit rows. Render both through the same
 * `auditValue`/`auditTarget` helpers `mcp.ts` already provides; there is no
 * Labeeb-specific reason for those to differ.
 */
export interface LabeebAuditEvent {
  /**
   * Nullable though the doc's only example has one: the doc gives no prose
   * guarantee that every row carries a timestamp, and MCP's structurally
   * identical (DynamoDB-backed) audit log hit exactly this gap — a real row
   * with no `timestamp` reached a bare `.replace()` inside a `.map` and
   * unmounted the whole React root. Absence of a documented `null` is not
   * proof of non-nullability; widen rather than assume.
   */
  timestamp: string | null;
  domain: string;
  action: string;
  actor: string | null;
  target: unknown;
  before: unknown;
  after: unknown;
  /**
   * Why the operator said they made this change.
   *
   * REQUIRED on every Labeeb change — the API refuses a write without one —
   * so its absence here means an old row, never a write that skipped it.
   */
  reason?: string | null;
}

export interface LabeebAuditPage {
  events?: LabeebAuditEvent[];
  count?: number;
  /** Opaque. Pass back verbatim as `?cursor=`; never construct or parse one. */
  cursor?: string;
}

/**
 * The four domains Labeeb's audit table keys on. Anything else is a 422.
 *
 * NOT the same set as MCP's (`tenant` | `quota` | `settings`) — Labeeb has
 * `flags` and `config` where MCP has `settings`. The two lists must stay
 * separate: a shared constant would offer an operator a filter the other
 * product answers 422 to.
 *
 * `GET /admin/catalog` also returns `audit_domains`; this is the fallback for
 * rendering the filter before the catalog has loaded.
 */
export const LABEEB_AUDIT_DOMAINS = ["standing", "settings", "quota", "rules"] as const;
export type LabeebAuditDomain = (typeof LABEEB_AUDIT_DOMAINS)[number];

/**
 * Fleet or per-domain audit list.
 *
 * The `days` window is sent ONLY for the fleet list, matching the API: the
 * domain routes carry no date filter and return older rows the top-level list
 * omits (docs/products/labeeb.md, "What was verified"). Sending `days` to a
 * route that ignores it would make the UI claim a window it is not applying.
 */
export function labeebAuditListPath(domain: LabeebAuditDomain | ""): string {
  const base = domain ? `/labeeb/admin/audit/${domain}` : "/labeeb/admin/audit";
  const q = new URLSearchParams();
  if (!domain) q.set("days", "7");
  q.set("limit", "50");
  return `${base}?${q.toString()}`;
}

/**
 * `data.events`, defaulting to `[]`.
 *
 * NOT named `eventsOf` — mcp.ts already exports that name for `AuditPage`,
 * and `Tenants.tsx`/`Tenant.tsx` import audit helpers from both products.
 */
export function labeebEventsOf(p: LabeebAuditPage | undefined | null): LabeebAuditEvent[] {
  return p?.events ?? [];
}

// ------------------------------------------------------------------ users --

/**
 * One person provisioned in a tenant.
 *
 * `name` and `email` are OPTIONAL and genuinely absent on some rows — they are
 * written by Token Exchange, not by this API — so render `user_id` as the
 * fallback rather than treating absence as an error.
 *
 * Foodics API tokens are never present: the index this reads *projects*
 * `ApiToken`/`RefreshToken`, so they arrive in the query result, and the
 * response is built from an explicit field list to keep them out. A test on
 * the product side pins that.
 */
export interface LabeebUser {
  user_id: string;
  name?: string;
  email?: string;
  is_owner?: boolean;
  first_seen?: string;
  last_seen?: string;
  /** `active` | `blocked`. */
  status?: string;
  /** What a block/unblock must send as `expected_version`. */
  version?: number;
  blocked_by?: string | null;
  blocked_at?: string | null;
  reason?: string | null;
}

export interface LabeebUsers {
  items?: LabeebUser[];
  next_cursor?: string | null;
}

/** `data.users`, defaulting to `[]`. */
export function labeebUsersOf(u: LabeebUsers | undefined | null): LabeebUser[] {
  return u?.items ?? [];
}

/** What to show for a user whose name and email were never written. */
export function labeebUserLabel(u: LabeebUser): string {
  return u.name || u.email || u.user_id;
}

// --------------------------------------------------------------- feedback --

/**
 * One row from the feedback table — which holds TWO different things.
 *
 * `customer` is a merchant's thumbs up/down on an answer. `tester` is a QA bug
 * report from the widget's overlay, and carries `category`, `severity`,
 * `screenshot_url` and `context`. Those keys are ABSENT, not null, on a
 * customer rating: a null severity would read as "unset" rather than "not
 * applicable".
 *
 * `comment` is free text a user typed, returned verbatim and untruncated.
 */
/**
 * One feedback report (`FeedbackOut`).
 *
 * THE RATING IS GONE. The old shape was a thumbs up/down with an overlapping
 * `tester` flag, a `category`, a `severity`, a bare `screenshot_url` and a
 * free-form `context` blob. The new API serves a report with a `source`, a
 * `status`, a `description`, and the thread and turn it came from — so a
 * report is now traceable to the conversation that produced it rather than
 * summarised as a sentiment.
 *
 * `screenshot` is an OBJECT with an EXPIRING url. Rendering it as a permanent
 * link would hand an operator a dead address later; `expires_at` says when.
 */
export interface LabeebFeedbackItem {
  report_id?: string;
  created_at?: string | null;
  description?: string;
  /** Who filed it, e.g. `customer` or `tester`. */
  source?: string;
  status?: string;
  thread_id?: string;
  turn_id?: string;
  model_version?: string | null;
  screenshot?: { url?: string; expires_at?: string } | null;
}

/**
 * One page of feedback (`FeedbackPageOut`).
 *
 * NO SUMMARY. The old shape carried positive/negative/total/tester counts,
 * with `tester` deliberately overlapping, and a `complete` flag warning that
 * a page could undercount. Neither exists now: there is no rating to total,
 * and the page is a plain cursor page.
 */
export interface LabeebFeedback {
  items?: LabeebFeedbackItem[];
  next_cursor?: string | null;
}

export function labeebFeedbackOf(f: LabeebFeedback | undefined | null): LabeebFeedbackItem[] {
  return f?.items ?? [];
}

/** A QA bug report rather than a merchant's rating. */
export function isTesterReport(i: LabeebFeedbackItem): boolean {
  return i.source === "tester";
}


/**
 * The environment's settings document (`EnvironmentSettingsOut`).
 *
 * `disabled_domains` / `disabled_plugins` are off for EVERY business — the
 * widest blast radius on this API. A tenant that enables one still cannot use
 * it, which is what `restricted_by` reports on the tenant side.
 */
export interface LabeebEnvironmentSettings {
  settings?: { disabled_domains?: string[]; disabled_plugins?: string[] };
  version?: number;
  by?: string | null;
  at?: string | null;
  reason?: string | null;
  schema_version?: string;
  model_version?: string | null;
}

/**
 * Deployment facts (`ConfigOut`) — NOT a key/value store.
 *
 * This route used to back a live-config editor. It now answers "what is this
 * API and how long does each change take", which is why `effects` is the only
 * field a page is likely to want: it is the server's own timing per resource,
 * and rendering it beats hardcoding "~60 seconds" in a dialog.
 */
export interface LabeebConfigInfo {
  admin_api_version?: string;
  model_version?: string;
  settings_schema_version?: string;
  default_tier?: string;
  base_path?: string;
  /**
   * `{backfill, covers}`. `backfill` stays the literal `"not-run"` until the
   * history backfill job exists, which means the audit holds ONLY changes
   * made through the admin API — not the full history of the business.
   * `covers` is the sentence to show an operator, so it is rendered rather
   * than summarised here.
   */
  audit_history?: { backfill?: string; covers?: string };
  effects?: { resource?: string; effect?: string }[];
}

// ------------------------------------------------------------------ rules --

/**
 * One autonomy condition.
 *
 * SIX KINDS, each with its own fields — a discriminated union on `kind`
 * rather than one wide optional bag, so a renderer cannot read `maximum` off
 * a `money_untouched` that never had one.
 */
export type LabeebCondition =
  | { kind: "at_most"; field: string; maximum: number }
  | { kind: "equals"; field: string; value: unknown }
  | { kind: "money_untouched" }
  | { kind: "touches_only"; fields: string[] }
  | { kind: "count_at_most"; field: string; maximum: number }
  | { kind: "delta_at_most"; field: string; maximum: number };

/** What a rule decides when it matches. Strictest wins: deny > approve > auto. */
export const RULE_EFFECTS = ["auto", "approve", "deny"] as const;
export type LabeebRuleEffect = (typeof RULE_EFFECTS)[number];

/**
 * Whether a rule actually decides anything.
 *
 * `enforce` — the rule decides.
 * `shadow`  — evaluated and recorded as what it WOULD have done, changing
 *             nothing. This is also how a rule is retired: there is no
 *             delete on this API, by design.
 */
export const RULE_MODES = ["enforce", "shadow"] as const;
export type LabeebRuleMode = (typeof RULE_MODES)[number];

/** What can set a rule off. */
export const RULE_TRIGGERS = ["chat", "event", "schedule"] as const;

/** The operations a scope can name. */
export const RULE_OPS = [
  "create",
  "get",
  "list",
  "update",
  "delete",
  "restore",
  "attach",
  "detach",
  "act",
] as const;

/** The roles a subject can name. Empty list means ANYONE. */
export const RULE_ROLES = ["owner", "staff", "service"] as const;

/**
 * One rule, as stored.
 *
 * `id` must equal the path's `rule_id` — the API refuses a mismatch — and
 * `model_version` must equal the catalog's, or it is a 422.
 */
export interface LabeebRuleDocument {
  id?: string;
  model_version?: string;
  /** `branches: null` means ALL branches, which is not the same as `[]`. */
  scope?: { target?: string; ops?: string[]; branches?: string[] | null };
  effect?: LabeebRuleEffect;
  triggers?: string[];
  /** Empty lists mean "anyone" — not "nobody". */
  subject?: { roles?: string[]; authorities?: string[]; users?: string[] };
  conditions?: LabeebCondition[];
  mode?: LabeebRuleMode;
  /** The rule author's own words, stored on the document. */
  reason?: string;
}

/** One rule with the version a write must send back. */
export interface LabeebRule {
  rule_id?: string;
  tenant_id?: string;
  rule?: LabeebRuleDocument;
  version?: number;
}

/** One page of rules (`RulePageOut`). */
export interface LabeebRules {
  items?: LabeebRule[];
  next_cursor?: string | null;
}

/** `data.items`, defaulting to `[]` rather than throwing on `.map`. */
export function labeebRulesOf(r: LabeebRules | undefined | null): LabeebRule[] {
  return r?.items ?? [];
}

/** Is this rule only observing? `shadow` is how a rule is retired here. */
export function isShadowRule(r: LabeebRule | undefined | null): boolean {
  return r?.rule?.mode === "shadow";
}

/**
 * One condition as a short phrase.
 *
 * Switches on `kind` so an unknown kind renders ITS NAME rather than nothing
 * — a condition the console has not learned yet still gates writes, and
 * hiding it would understate what a rule does.
 */
export function describeCondition(c: LabeebCondition): string {
  switch (c.kind) {
    case "at_most":
      return `${c.field} at most ${c.maximum}`;
    case "count_at_most":
      return `${c.field} count at most ${c.maximum}`;
    case "delta_at_most":
      return `${c.field} changes by at most ${c.maximum}`;
    case "equals":
      return `${c.field} equals ${String(c.value)}`;
    case "money_untouched":
      return "money untouched";
    case "touches_only":
      return `touches only ${(c.fields ?? []).join(", ")}`;
    default:
      return (c as { kind?: string }).kind ?? "unknown condition";
  }
}

// ------------------------------------------------------------------ paths --
//
// Every Labeeb path in one place. Pages previously spelled these as literals,
// so a route rename meant grepping `"/labeeb/admin/…"` across pages/ rather
// than editing one module — the same reason api/mcp.ts exports its builders.
//
// All 27 routes of the new admin API (specs/admin/openapi.json in the
// foodics-labeeb repo, mirrored by PRODUCT_ROUTES in
// infrastructure/infra/products.py). The gateway forwards the whole path, so
// the Lambda receives `/labeeb/admin/…`.

/** `GET /admin/catalog` — every valid value a write on this API accepts. */
export const LABEEB_CATALOG = "/labeeb/admin/catalog";

/** `GET /admin/tenants` — one page of businesses, newest cursor last. */
export const LABEEB_TENANTS = "/labeeb/admin/tenants";

/** `GET /admin/tiers` — one page of tier envelopes. */
export const LABEEB_TIERS = "/labeeb/admin/tiers";

/**
 * `GET /admin/config` — deployment facts, NOT a key/value store.
 *
 * It used to be the live-config editor's backing route. It now returns the
 * API's own versions, the default tier, the audit history window and the
 * per-resource effect timings. Nothing here is writable, and the key/value
 * shape the console edited is gone along with `PATCH`/`DELETE
 * /admin/config/{key}`.
 */
export const LABEEB_CONFIG = "/labeeb/admin/config";

/** `GET`/`PATCH /admin/environment/settings` — fleet-wide domain/plugin gates. */
export const LABEEB_ENVIRONMENT_SETTINGS = "/labeeb/admin/environment/settings";

/** `GET /admin/tenant/{tenant_id}` — one business, with its profile and standing. */
export function labeebTenantPath(tenantId: string): string {
  return `/labeeb/admin/tenant/${encodeURIComponent(tenantId)}`;
}

/** `GET /admin/tenant/{tenant_id}/usage` — read-only counters, no write path. */
export function labeebTenantUsagePath(tenantId: string): string {
  return `${labeebTenantPath(tenantId)}/usage`;
}

/** `GET /admin/tenant/{tenant_id}/users` — the staff Labeeb has seen. */
export function labeebTenantUsersPath(tenantId: string): string {
  return `${labeebTenantPath(tenantId)}/users`;
}

/**
 * `GET /admin/tenant/{tenant_id}/feedback`.
 *
 * There is deliberately no fleet-wide equivalent for either this or /users: a
 * list spraying every merchant's staff names and emails is a different privacy
 * posture from looking up the tenant you are already supporting.
 */
export function labeebTenantFeedbackPath(tenantId: string): string {
  return `${labeebTenantPath(tenantId)}/feedback`;
}

/** `GET /admin/tenant/{tenant_id}/history` — this business's own change log. */
export function labeebTenantHistoryPath(tenantId: string): string {
  return `${labeebTenantPath(tenantId)}/history`;
}

/**
 * `GET`/`PATCH /admin/tenant/{tenant_id}/standing`.
 *
 * Labeeb's first real enforcement lever. A suspended business has its NEXT
 * request refused — this is not a label on a table nobody reads, which is
 * what the `LabeebUserMgmt` stack's `PUT /admin/users/{userId}` was.
 */
export function labeebStandingPath(tenantId: string): string {
  return `${labeebTenantPath(tenantId)}/standing`;
}

/** `PATCH /admin/tenant/{tenant_id}/users/{user_id}/standing` — block one user. */
export function labeebUserStandingPath(tenantId: string, userId: string): string {
  return `${labeebTenantUsersPath(tenantId)}/${encodeURIComponent(userId)}/standing`;
}

/** `GET`/`PATCH /admin/tenant/{tenant_id}/settings` — which domains may run. */
export function labeebTenantSettingsPath(tenantId: string): string {
  return `${labeebTenantPath(tenantId)}/settings`;
}

/** `GET`/`PATCH /admin/tenant/{tenant_id}/quota` — this business's tier. */
export function labeebTenantQuotaPath(tenantId: string): string {
  return `${labeebTenantPath(tenantId)}/quota`;
}

/** `GET /admin/tenant/{tenant_id}/rules` — the business's autonomy rules. */
export function labeebRulesPath(tenantId: string): string {
  return `${labeebTenantPath(tenantId)}/rules`;
}

/** `GET`/`PATCH /admin/tenant/{tenant_id}/rules/{rule_id}` — one rule. */
export function labeebRulePath(tenantId: string, ruleId: string): string {
  return `${labeebRulesPath(tenantId)}/${encodeURIComponent(ruleId)}`;
}

/**
 * `GET`/`PATCH /admin/tiers/{tier_id}`.
 *
 * The GET is NEW. The console used to read the whole list and select a row,
 * because no by-id route existed; that workaround is gone.
 */
export function labeebTierPath(tierId: string): string {
  return `/labeeb/admin/tiers/${encodeURIComponent(tierId)}`;
}
